import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PaperKbdComponent } from '@ricoh-paper/angular';
import { LocaleService } from '../../../../../core/services/locale.service';

/** One hint: a chord and what it does. */
interface CmdkHint {
  readonly keys: readonly { readonly keys: string; readonly mod: boolean; readonly label: string }[];
  readonly text: string;
}

/**
 * The hint row, and the palette's ONLY polite live region.
 *
 * AI CONTEXT — one live region, deliberately
 *
 * The legacy palette has two (`command-palette.component.html:60` and `:83`),
 * and both can update in the same tick, so a screen reader reads a result
 * count over an AI progress message. Adding a third for the voice state would
 * make it three. Here the result count is the single `role="status"` and every
 * other transient announcement — listening started, link copied, booking
 * undone — goes through CDK's `LiveAnnouncer`, which serialises them.
 *
 * The hint set SWAPS by mode rather than growing. Showing every binding at
 * once is a cheatsheet, and the palette already has one behind `?`.
 */
@Component({
  selector: 'im-cmdk-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperKbdComponent],
  templateUrl: './cmdk-footer.component.html',
  styleUrl: './cmdk-footer.component.scss',
})
export class CmdkFooterComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  readonly resultCount = input(0);
  /** True while ⌘ is held — the hints swap to what the digits do. */
  readonly modifierHeld = input(false);
  /** True inside a flow — the hints become Confirm / Next field / Back. */
  readonly inFlow = input(false);
  /** True while dictating. */
  readonly listening = input(false);
  readonly aiEnabled = input(false);
  /** An answer is on screen, so the chord that resizes it is live. */
  readonly hasAnswer = input(false);

  protected readonly hints = computed<CmdkHint[]>(() => {
    this.locale.locale();
    const t = (key: string): string => this.transloco.translate(key);
    const key = (keys: string, label: string, mod = false): CmdkHint['keys'][number] =>
      ({ keys, mod, label });

    if (this.listening()) {
      const listeningHints: CmdkHint[] = [
        { keys: [key('Esc', 'Escape')], text: t('shell.cmdk.hint.stop_listening') },
      ];
      if (this.aiEnabled()) {
        listeningHints.push({ keys: [], text: t('shell.cmdk.hint.say_ask_ai') });
      }
      return listeningHints;
    }

    if (this.inFlow()) {
      return [
        { keys: [key('↵', 'Enter')], text: t('shell.cmdk.hint.confirm') },
        { keys: [key('Tab', 'Tab')], text: t('shell.cmdk.hint.next_field') },
        { keys: [key('Esc', 'Escape')], text: t('shell.cmdk.hint.back') },
      ];
    }

    if (this.modifierHeld()) {
      const held: CmdkHint[] = [
        { keys: [key('1–9', 'One to nine', true)], text: t('shell.cmdk.hint.open_result') },
        { keys: [key('C', 'C', true)], text: t('shell.cmdk.hint.copy_link') },
      ];
      if (this.aiEnabled()) {
        held.push({ keys: [key('↵', 'Enter', true)], text: t('shell.cmdk.hint.ask') });
        held.push({
          keys: [key('J', 'J', true)],
          text: t('shell.cmdk.hint.open_ai'),
        });
        // Both of these are real chords in cmdk-keymap.ts and neither was
        // advertised anywhere. They are gated exactly as the keymap gates
        // them, so the footer never names a chord that would do nothing.
        held.push({ keys: [key('⇧↵', 'Shift Enter', true)], text: t('shell.cmdk.hint.continue_in_chat') });
        if (this.hasAnswer()) {
          held.push({ keys: [key('.', 'Full stop', true)], text: t('shell.cmdk.hint.more_room') });
        }
      }
      return held;
    }

    const base: CmdkHint[] = [
      { keys: [key('↵', 'Enter')], text: t('shell.cmdk.hint.open') },
      { keys: [key('↑', 'Up'), key('↓', 'Down')], text: t('shell.cmdk.hint.navigate') },
      { keys: [key('→', 'Right')], text: t('shell.cmdk.hint.actions') },
      { keys: [key('Tab', 'Tab')], text: t('shell.cmdk.hint.filter') },
    ];
    if (this.aiEnabled()) {
      base.push({ keys: [key('↵', 'Enter', true)], text: t('shell.cmdk.hint.ask') });
    }
    base.push({ keys: [key('?', 'Question mark')], text: t('shell.cmdk.hint.help') });
    return base;
  });

  /**
   * The count is suppressed at zero. "0 results" beside a "No matches" panel
   * says the same thing twice, and the emptier of the two is the one that
   * reads as a fault.
   */
  protected readonly showCount = computed(() => this.resultCount() > 0);

  protected readonly countLabel = computed(() => {
    this.locale.locale();
    const count = this.resultCount();
    return this.transloco.translate(
      count === 1 ? 'shell.cmdk.count.one' : 'shell.cmdk.count.other',
      { count },
    );
  });
}
